import React, { Fragment, memo, useEffect, useMemo, useRef, useState } from 'react';

import { isObject } from 'formik';
import Select from 'react-select';

import { SEND_FOR_COMMITMENT_OPTION, isPlaceholderAllocation } from './allocationHelpers';
import BudgetLineSelect, { BudgetLinePath, LegacyMarker, buildBudgetLineGroups, suggestedBudgetLineIds } from './BudgetLineSelect';
import BulkApplyPanel from './BulkApplyPanel';
import { FLAT_SELECT_CLASS, flat } from './reconciliationSelectStyles';

import { formatFigure, formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import CheckboxInput from 'CommonComponents/CheckboxInput';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import RowActionsMenu from 'CommonComponents/RowActionsMenu';
import TextButton from 'CommonComponents/TextButton';
import Tooltip from 'CommonComponents/Tooltip';

import { useToast } from 'Hooks/useToast';

import ArchiveIcon from 'Assets/icons/box.svg';
import CircleCheckIcon from 'Assets/icons/circle-check.svg';
import CircleCancelIcon from 'Assets/icons/circle-xmark.svg';
import EditIcon from 'Assets/icons/edit.svg';
import EyeIcon from 'Assets/icons/eye.svg';
import PlusCircleIcon from 'Assets/icons/plus.svg';
import RefreshIcon from 'Assets/icons/refresh-sync.svg';
import TrashIcon from 'Assets/icons/trash.svg';

/**
 * Opt-in single-line clipping for a body cell, defined in sass/tables/_style.scss.
 *
 * Only the columns carrying unbounded text ask for it. The allocation cells below
 * take it unconditionally — their edit controls are react-selects whose menus are
 * portalled to <body>, so the closed control is all that sits inside the cell and
 * clipping it to the column is the intent. The general-info columns in
 * ActualCostsAllocationRow take it in READ mode only: their controls (text inputs,
 * the date picker, native selects) are NOT portalled, and a control wider than the
 * cell would lose its right-hand end.
 */
export const TRUNCATE_CELL_CLASS = 'reconciliation-cell--truncate';

// Allocation amounts are money to two places, and the row's own availableAmount
// already rounds there — compare at the same precision so a float artefact does
// not read as an over-allocation.
const round2 = (value) => Math.round(value * 100) / 100;

const ActualCostsAllocationSubRow = memo(function ActualCostsAllocationSubRow(props) {
    const [editing, setEditing] = useState(false);
    const [isNew, setIsNew] = useState(isPlaceholderAllocation(props.data));
    const [departmentOptions, setDepartmentOptions] = useState([]);
    const [contractReferenceOptions, setContractReferenceOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    // The Amount cell shows a formatted figure at rest and the raw number while
    // being edited — a formatted value cannot be typed into.
    const [amountFocused, setAmountFocused] = useState(false);

    const toast = useToast();

    const getTypeKey = (type) => {
        return type === 'Non-Workforce' ? 'non_workforce' : 'workforce';
    };

    // The transaction's own supplier, as the suggested-match group compares it.
    // The linked supplier record wins; an import that never matched one carries
    // only the raw string, and that is still the name the reconciler recognises.
    const actualCostSupplier = (props.actualCosts?.supplier || props.actualCosts?.supplier_name || '').trim().toLowerCase();

    // A negative balance cannot be committed, so a credit note can never be sent
    // for commitment. The server rejects it with a 422 either way; disabling the
    // option is what stops the reconciler getting there.
    const isCreditTransaction = Number(props.actualCosts?.amount) < 0;

    // Send for commitment is expenditure-only. Absent rather than disabled on a
    // revenue row: a disabled option with a tooltip reads as "not yet", and
    // invites the question on every revenue row forever, where this is a side of
    // the system the feature is never coming to.
    const isRevenueTransaction = props.actualCosts?.type === 'revenue';

    const getContractReferenceOptions = () => {
        const options = [];
        const type = getTypeKey(props.data.type);
        const suggestions = props.suggestions[type];
        const departmentId = props.data.typed_allocatable_id;
        const blId = props.data.typed_valuable_id;

        const mapToOptions = (arr) =>
            (arr || [])
                .filter((c) => {
                    if (!c.closed) {
                        return true;
                    }

                    return !!props.data.contractable_id && props.data.contractable_id === c.id;
                })
                .map((c) => ({
                    value: c.typed_id ?? c.id,
                    label: c.reference,
                    tooltipData: {
                        supplierName: c.supplier?.name || '-',
                        department: c.departments_names?.join(', '),
                        contractValue: formatToFixedMoneyWithLocale(c.value),
                        actuals: formatToFixedMoneyWithLocale(c.actual_spend),
                        variance: c.variance ? formatToFixedMoneyWithLocale(c.variance) : null,
                    },
                }));

        const recommended = mapToOptions(props.contracts?.recommended);
        const others = mapToOptions(props.contracts?.others);

        // The tail groups are appended by every path below, INCLUDING the one
        // where the event has no contracts at all — which used to return an empty
        // option set and is exactly the event most likely to need the flag. That
        // also keeps "No contract" reachable, so a flagged row always has a way
        // back off the list.
        const otherContractsGroup = {
            label: $t('Other Contracts'),
            options: [{ label: $t('No contract'), value: null }, ...others],
        };

        // Two of the server-side guards in SyncAllocationsRequest, mirrored here
        // so the reconciler is stopped at the option rather than at a 422
        // explaining a rule the screen never showed. The reasons stay distinct: a
        // credit can never be committed at all, a missing budget line is one
        // selection away, and the tooltip is where that difference is learned.
        // The revenue guard is not mirrored as a reason — that group is simply
        // not built, see tailGroups below.
        const noBudgetLine = !blId;
        const sendForCommitmentGroup = {
            label: $t('Other Actions'),
            options: [
                {
                    value: SEND_FOR_COMMITMENT_OPTION,
                    label: $t('Send for commitment'),
                    isDisabled: isCreditTransaction || noBudgetLine,
                    // react-select drops `title` on a disabled option, so the
                    // explanation is rendered by formatContractOptionLabel.
                    disabledReason: isCreditTransaction
                        ? $t('A credit cannot be sent for commitment.')
                        : $t('Select a budget line before sending this allocation for commitment.'),
                },
            ],
        };

        // What every return path below appends. On a revenue row the commitment
        // group is not in it at all, so the option never renders.
        const tailGroups = isRevenueTransaction ? [otherContractsGroup] : [otherContractsGroup, sendForCommitmentGroup];

        const emptySuggestions =
            !isObject(suggestions) ||
            !(departmentId in suggestions) ||
            !isObject(suggestions[departmentId].budgetLines) ||
            !(blId in suggestions[departmentId].budgetLines) ||
            !isObject(suggestions[departmentId].budgetLines[blId]);

        if (emptySuggestions) {
            if (recommended.length) {
                options.push({
                    label: $t('Recommended'),
                    options: recommended,
                });
            }

            options.push(...tailGroups);

            return options;
        }

        const suggestedOptions = {
            label: $t('Suggested'),
            options: [],
        };

        // The same budget-line contracts, narrowed to the open ones raised against
        // this transaction's own supplier. Offered first and as one click each:
        // the reconciler about to flag an unmatched actual is being shown the
        // contracts it most likely belongs to, and picking one links it — which
        // clears the flag — instead of adding it to the commitment list.
        const supplierMatchOptions = {
            label: $t('Matching Supplier'),
            options: [],
        };

        suggestions[departmentId].budgetLines[blId].contracts.forEach((contractId) => {
            const contract = findContract(contractId);
            if (!contract) {
                return;
            }

            const canBeAdded = !contract.closed || (!!props.data.contractable_id && props.data.contractable_id === contract.id);
            if (!canBeAdded) {
                return;
            }

            suggestedOptions.options.push({ value: contract.typed_id, label: contract.reference });

            const supplierMatches = !!actualCostSupplier && (contract.supplier?.name || '').trim().toLowerCase() === actualCostSupplier;
            if (!contract.closed && supplierMatches) {
                supplierMatchOptions.options.push({ value: contract.typed_id, label: contract.reference });
            }
        });

        if (supplierMatchOptions.options.length > 0) {
            options.push(supplierMatchOptions);
        }

        if (suggestedOptions.options.length > 0) {
            options.push(suggestedOptions);
        }

        if (recommended.length) {
            options.push({
                label: $t('Recommended'),
                options: recommended,
            });
        }

        options.push(...tailGroups);

        return options;
    };

    const getDepartmentsOptions = () => {
        const options = [];

        const type = getTypeKey(props.data.type);
        const suggestions = props.suggestions[type];

        if (!isObject(suggestions)) {
            const otherOptions = {
                options: [],
            };
            props.departments.forEach((department) => {
                otherOptions.options.push({ value: department.typed_id, label: department.name });
            });
            options.push(otherOptions);

            return options;
        }

        const excludedIds = [];
        const suggestedOptions = {
            label: $t('Suggested'),
            options: [],
        };

        Object.keys(suggestions).forEach((departmentId) => {
            const dep = props.departments.find((item) => {
                return item.typed_id === departmentId;
            });

            if (dep) {
                excludedIds.push(departmentId);
                suggestedOptions.options.push({ value: dep.typed_id, label: dep.name });
            }
        });

        if (suggestedOptions.options.length > 0) {
            options.push(suggestedOptions);
        }

        const otherOptions = {
            label: $t('Other Departments / Cost Centres'),
            options: [],
        };
        props.departments.forEach((department) => {
            if (!excludedIds.includes(department.typed_id)) {
                otherOptions.options.push({ value: department.typed_id, label: department.name });
            }
        });

        if (otherOptions.options.length > 0) {
            options.push(otherOptions);
        }

        return options;
    };

    const getDefaultValue = (id, options) => {
        if (!id) {
            return '';
        }

        let res = '';
        options.forEach((set) => {
            set.options.forEach((item) => {
                if (item.value === id) {
                    res = item;
                }
            });
        });

        return res;
    };

    // The lines arrive already narrowed by department and by Workforce /
    // Non-Workforce; grouping them by their project (or owning department / cost
    // centre) is pure presentation, so it is derived rather than stored.
    const budgetLineOptions = useMemo(
        () =>
            buildBudgetLineGroups(
                props.directLineItems,
                suggestedBudgetLineIds(props.suggestions, getTypeKey(props.data.type), props.data.typed_allocatable_id),
            ),
        [props.directLineItems, props.suggestions, props.data.type, props.data.typed_allocatable_id],
    );

    const selectedBudgetLine = getDefaultValue(props.data.typed_valuable_id, budgetLineOptions);

    useEffect(() => {
        setEditing(props.editedRow);
    }, [props.editedRow]);

    useEffect(() => {
        setDepartmentOptions(getDepartmentsOptions());
    }, [props.data.type]);

    // A selection that is no longer among the options has to go — keeping it
    // would save an allocation against a line the row can no longer reach. What
    // must NOT happen is losing it silently, so the user is told why. The first
    // pass is exempt: it only resolves what was already persisted, and 40 rows
    // announcing themselves on page load would be noise, not information.
    const clearGuardPrimedRef = useRef(false);

    useEffect(() => {
        // Priming happens BELOW this guard on purpose. The options arrive from
        // /reconciliation/locations, which is the slow one of the four requests
        // ReconciliationIndex fires on mount, so the first run of this effect
        // normally bails here with an empty option set. Priming before the bail
        // would spend the exemption on a run that never compared anything, and the
        // first real comparison would then toast "the budget line was cleared" for
        // every unresolvable row on the page.
        if (!props.data.typed_valuable_id || budgetLineOptions.length === 0) {
            return;
        }

        const primed = clearGuardPrimedRef.current;
        clearGuardPrimedRef.current = true;

        const found = budgetLineOptions.some((group) => group.options?.some((opt) => opt.value === props.data.typed_valuable_id));
        if (!found) {
            props.changeValuable(props.index, { value: null });

            if (primed) {
                toast.warning($t('The budget line was cleared because the department or type changed. Please choose a new one.'));
            }
        }
    }, [budgetLineOptions]);

    // `props.actualCosts.amount` and `.supplier` are listed because the options
    // derive from them — the credit-note disable and the Matching Supplier group.
    // Today they are covered incidentally: contractLists() builds a fresh object
    // every render, so `props.contracts` changes identity and re-runs this. Both
    // fields are inline-editable, so memoizing contractLists later would freeze
    // both behaviours here with nothing to point at.
    useEffect(() => {
        setContractReferenceOptions(getContractReferenceOptions());
    }, [
        props.contracts,
        props.data.typed_valuable_id,
        props.data.typed_allocatable_id,
        props.data.type,
        props.actualCosts?.amount,
        props.actualCosts?.supplier,
        props.actualCosts?.type,
    ]);

    useEffect(() => {
        setIsNew(isPlaceholderAllocation(props.data));
    }, [props.data]);

    const departmentName = (id) => {
        const department = props.departments.find((item) => {
            return item.typed_id === id;
        });

        return department?.name;
    };

    // Type and Transaction were native <select> elements. They are react-select
    // now so all five selectors share the flat treatment; the change is purely
    // visual, which is why both hand `changeType` / `changeTransactionType` the
    // native `{ target: { value } }` shape they already expect.
    const typeOptions = (props.types ?? []).map((item) => ({ value: item, label: item }));

    const transactionTypeOptions = Object.keys(props.transactionTypes ?? {}).map((key) => ({ value: key, label: props.transactionTypes[key] }));

    const findFlatOption = (options, value) => options.find((option) => option.value === value) ?? '';

    const findContract = (id) => {
        const pools = [...(props.contracts?.recommended || []), ...(props.contracts?.others || [])];
        return pools.find((c) => (c.typed_id ?? c.id) === id);
    };

    const contractReference = (id) => {
        const c = findContract(id);
        return c?.reference;
    };

    // Flagged and linked-to-a-contract are mutually exclusive, server-side too:
    // the flag IS the reference a flagged row shows, in the closed cell and as
    // the dropdown's selected option alike.
    const contractSelectionValue = props.data.send_for_commitment ? SEND_FOR_COMMITMENT_OPTION : props.data.typed_contractable_id;
    const contractReferenceLabel = props.data.send_for_commitment ? $t('Send for commitment') : contractReference(props.data.typed_contractable_id);

    const formatContractOptionLabel = (option, { context }) => {
        // Why the option cannot be picked. react-select strips `title` from a
        // disabled option, so a plain attribute would say nothing.
        if (context === 'menu' && option.isDisabled && option.disabledReason) {
            return (
                <Tooltip placement="right" allowTooltip={true} tooltipType="text" offset={[0, 18]} tooltipContent={option.disabledReason}>
                    <div>{option.label}</div>
                </Tooltip>
            );
        }

        if (context === 'menu' && option.tooltipData) {
            return (
                <Tooltip
                    placement="right"
                    allowTooltip={true}
                    tooltipType="text"
                    offset={[0, 18]}
                    tooltipContent={
                        <div className="tw-text-xs tw-space-y-1">
                            <div>
                                <span className="tw-font-semibold">{$t('Supplier Name')}:</span> {option.tooltipData.supplierName}
                            </div>
                            <div>
                                <span className="tw-font-semibold">{$t('Department')}:</span> {option.tooltipData.department || '-'}
                            </div>
                            <div>
                                <span className="tw-font-semibold">{$t('Contract Value')}:</span> {option.tooltipData.contractValue}
                            </div>
                            <div>
                                <span className="tw-font-semibold">{$t('Actuals')}:</span> {option.tooltipData.actuals}
                            </div>
                            {!!option.tooltipData.variance && (
                                <div>
                                    <span className="tw-font-semibold">{$t('Variance')}:</span> {option.tooltipData.variance}
                                </div>
                            )}
                        </div>
                    }
                >
                    <div>{option.label}</div>
                </Tooltip>
            );
        }
        return option.label;
    };

    // The Amount input was <input type="number">, which cannot render "1,234" or
    // "(500)". As a text input it can, so the raw number is only exposed while the
    // field has focus.
    //
    // Dropping everything outside [0-9.-] is not enough on its own: it still admits
    // '1.500.00', '5-3', '--5' and a bare '-', and changeAmount only clamps against
    // the maximum, so the malformed string was stored verbatim and reached a float
    // property on save. Keeping only a leading '-' and a single '.' is what the
    // number input used to do for free.
    const onAmountChange = (event) => {
        const stripped = event.target.value.replace(/[^0-9.-]/g, '');
        const wellFormed = stripped.match(/^-?\d*(\.\d*)?/);

        props.changeAmount(props.index, { target: { value: wellFormed ? wellFormed[0] : '' } });
    };

    // min / max were HTML attributes on the number input: advisory validation the
    // browser flagged but never blocked, with the real clamp living in the row's
    // changeAmount. A text input has no such attributes, so the same bound is
    // stated explicitly here — still advisory, so a transaction that is already
    // over-allocated stays editable instead of being silently rewritten.
    //
    // `availableAmount` is the headroom left after EVERY allocation, this one
    // included, so what this line may hold is that plus what it already holds —
    // the same figure the row clamps against as availableAmount(index). Using the
    // prop as-is would cap a fully-allocated row at zero and flag every correct
    // row as invalid.
    const amountHeadroom = round2(Number(props.availableAmount || 0) + Number(props.data.amount || 0));
    const amountMin = amountHeadroom > 0 ? 0 : amountHeadroom;
    const amountMax = amountHeadroom > 0 ? amountHeadroom : 0;
    const amountNumeric = round2(Number(props.data.amount));
    // Number('') is 0 and Number('-') is NaN, so an emptied or half-typed field has
    // to be flagged here too — not just out-of-range — or it passes this guard,
    // reaches confirm, and fails as a 422 the row cannot recover from on its own.
    const amountOutOfRange = !isFinite(amountNumeric) || amountNumeric < amountMin || amountNumeric > amountMax;

    /**
     * The bulk-apply trigger for one allocation cell.
     *
     * Shown only where the cell holds a value the user can SEE — there is nothing
     * to copy from an empty cell, and nothing to copy from a value that is not on
     * screen. It appears on hover, which is what the enclosing cell's `tw-group`
     * drives, and is held visible on a row the reconciler opened for editing so it
     * is there the moment they set the value.
     *
     * And only for a column the table it is on offers: the reconciled table offers
     * contract reference alone (see BULK_APPLY_COLUMNS_RECONCILED).
     */
    const applyTrigger = (column, columnLabel, cellValue, cellValueLabel) => {
        // A row with no persisted allocation still carries a synthetic `new_` one,
        // which `defaultAllocationData()` seeds from an ERP suggestion or the row's
        // own department — so `cellValue` reads as truthy on it. Whether that value
        // is on screen depends on the mode, and only the READ mode hides it: the
        // spans below blank an isNew cell. The table opens every unallocated row in
        // its editor (see the editedRows seed in ActualCostsAllocationTable), and
        // there the value is showing in the select, as copyable as any other — which
        // is most of the unreconciled list on a freshly imported event, and was the
        // whole population this suppressed the trigger on.
        if (!props.bulkApply?.enabled || !props.bulkApply.columns?.includes(column) || !cellValue || (isNew && !editing)) {
            return null;
        }

        return (
            <BulkApplyPanel
                column={column}
                columnLabel={columnLabel}
                value={cellValue}
                valueLabel={cellValueLabel}
                sourceRow={props.actualCosts}
                // Pinned open on a row the reconciler opened deliberately. An
                // unallocated row is opened FOR them, and there are as many of those
                // as there are unallocated imports — pinning it on each would run a
                // column of icons down the page (Type alone is never empty), so those
                // keep the hover treatment every other filled cell already has.
                //
                // `!isNew` cannot be that signal by itself: a bulk apply gives an
                // auto-opened row real allocation ids without the reconciler ever
                // asking to edit it, which flips `isNew` false on every row the apply
                // just touched and would pin the icon there too. `manuallyOpenedRow`
                // is the table's own record of whether THIS row's edit mode came from
                // an explicit setEdited() call (a click, or Edit from the row menu)
                // rather than the auto-seed for having nothing allocated — see
                // ActualCostsAllocationTable — so it keeps meaning "the reconciler
                // opened this" across a bulk-apply refetch.
                visible={editing && props.manuallyOpenedRow}
                available={props.bulkApply.available}
                filters={props.bulkApply.filters}
                actualCostType={props.bulkApply.actualCostType}
                isReconciled={props.bulkApply.isReconciled}
                urls={props.bulkApply.urls}
                defaultMatchColumn={props.bulkApply.defaultMatchColumn}
                onApplied={props.bulkApply.onApplied}
                toast={toast}
            />
        );
    };

    // Selection, confirm and cancel stay inline in the cell — they are the
    // reconciler's primary loop. Everything else moves behind the overflow menu so
    // the Actions column stops being eight icons wide.
    const rowActions = [
        {
            key: 'split',
            label: $t('Split Allocation'),
            icon: <PlusCircleIcon className="tw-h-3 tw-w-3 tw-text-green-500" />,
            onClick: () => props.processClickAdd(),
        },
        !editing && {
            key: 'edit',
            label: $t('Edit'),
            icon: <EditIcon className="tw-h-3 tw-w-3" />,
            onClick: () => props.setEditedRow(),
        },
        // Archive acts on the whole transaction, so it belongs to the row rather
        // than to any one allocation — offered on the first sub-row only. Listed
        // above Delete: it is the reversible one of the two.
        props.index === 0 &&
            !!props.archiveActualCost && {
                key: 'archive',
                label: $t('Archive'),
                icon: <ArchiveIcon className="tw-h-3 tw-w-3 tw-text-charcoal-75" />,
                onClick: () => props.archiveActualCost(props.actualCosts),
            },
        {
            key: 'delete',
            label: $t('Delete Allocation'),
            icon: <TrashIcon className="tw-h-3 tw-w-3 tw-text-red-500" />,
            onClick: () => props.processClickDelete(props.index),
        },
        !!props.checkActualCostsStatus && {
            key: 'check-status',
            label: $t('Check Status'),
            icon: <EyeIcon className="tw-h-3 tw-w-3 tw-text-green-500" />,
            onClick: () => props.checkActualCostsStatus(),
        },
        !!props.refresh && {
            key: 'refresh',
            label: $t('Refresh'),
            icon: <RefreshIcon className="tw-h-3 tw-w-3 tw-text-green-500" />,
            onClick: () => props.refresh(),
        },
    ];

    return (
        <Fragment>
            {props.displayedColumns.department && (
                <td className={`${TRUNCATE_CELL_CLASS} tw-group`} onClick={props.setEditedRow}>
                    <div className="tw-flex tw-items-center tw-gap-x-1">
                        <div className="tw-flex-1 tw-min-w-0">
                            {editing && (
                                <Select
                                    options={departmentOptions}
                                    value={getDefaultValue(props.data.typed_allocatable_id, departmentOptions)}
                                    onChange={(e) => props.changeAllocation(props.index, e)}
                                    placeholder={$t('Select…')}
                                    className={FLAT_SELECT_CLASS}
                                    classNamePrefix="react-select"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={flat}
                                />
                            )}
                            {!editing && (
                                <span className={isNew || departmentName(props.data.typed_allocatable_id) ? undefined : 'tw-text-charcoal-50'}>
                                    {isNew ? '' : departmentName(props.data.typed_allocatable_id) || $t('Select…')}
                                </span>
                            )}
                        </div>
                        {applyTrigger(
                            'department',
                            $t('Department / Cost Centre'),
                            props.data.typed_allocatable_id,
                            departmentName(props.data.typed_allocatable_id),
                        )}
                    </div>
                </td>
            )}
            {props.displayedColumns.type && (
                <td className="tw-group" onClick={props.setEditedRow}>
                    <div className="tw-flex tw-items-center tw-gap-x-1">
                        <div className="tw-flex-1 tw-min-w-0">
                            {editing && (
                                <Select
                                    options={typeOptions}
                                    value={findFlatOption(typeOptions, props.data.type)}
                                    onChange={(option) => props.changeType(props.index, { target: { value: option.value } })}
                                    placeholder={$t('Select…')}
                                    className={FLAT_SELECT_CLASS}
                                    classNamePrefix="react-select"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    isSearchable={false}
                                    styles={flat}
                                />
                            )}
                            {!editing && <span>{isNew ? '' : props.data.type}</span>}
                        </div>
                        {applyTrigger('type', $t('Type'), props.data.type, props.data.type)}
                    </div>
                </td>
            )}
            {props.displayedColumns.budgetLine && (
                <td className={`${TRUNCATE_CELL_CLASS} tw-group`} onClick={props.setEditedRow}>
                    <div className="tw-flex tw-items-center tw-gap-x-1">
                        <div className="tw-flex-1 tw-min-w-0">
                            {editing && (
                                <BudgetLineSelect
                                    groups={budgetLineOptions}
                                    value={selectedBudgetLine}
                                    onChange={(e) => props.changeValuable(props.index, e)}
                                />
                            )}
                            {!editing &&
                                !isNew &&
                                (selectedBudgetLine ? (
                                    <BudgetLinePath
                                        parentLabel={selectedBudgetLine.is_parent ? null : selectedBudgetLine.group_label}
                                        name={selectedBudgetLine.label}
                                        markers={
                                            selectedBudgetLine.is_legacy_child ? (
                                                <LegacyMarker parentLabel={selectedBudgetLine.legacy_parent_label} withTooltip={false} />
                                            ) : null
                                        }
                                    />
                                ) : (
                                    <span className="tw-text-charcoal-50">{$t('Select…')}</span>
                                ))}
                        </div>
                        {applyTrigger('budget_line', $t('Budget Line'), props.data.typed_valuable_id, selectedBudgetLine?.label)}
                    </div>
                </td>
            )}
            {props.displayedColumns.transactionType && (
                <td onClick={props.setEditedRow}>
                    {editing && (
                        <Select
                            options={transactionTypeOptions}
                            value={findFlatOption(transactionTypeOptions, props.data.transaction_type)}
                            onChange={(option) => props.changeTransactionType(props.index, { target: { value: option.value } })}
                            placeholder={$t('Select…')}
                            className={FLAT_SELECT_CLASS}
                            classNamePrefix="react-select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            isSearchable={false}
                            styles={flat}
                        />
                    )}
                    {!editing && <span>{props.transactionTypes[props.data.transaction_type]}</span>}
                </td>
            )}
            {props.displayedColumns.contractReference && (
                <td className={`${TRUNCATE_CELL_CLASS} tw-group`} onClick={props.setEditedRow}>
                    <div className="tw-flex tw-items-center tw-gap-x-1">
                        <div className="tw-flex-1 tw-min-w-0">
                            {editing && (
                                <Select
                                    options={contractReferenceOptions}
                                    value={getDefaultValue(contractSelectionValue, contractReferenceOptions)}
                                    onChange={(e) => props.changeContractable(props.index, e)}
                                    formatOptionLabel={formatContractOptionLabel}
                                    placeholder={$t('Select…')}
                                    className={FLAT_SELECT_CLASS}
                                    classNamePrefix="react-select"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={flat}
                                />
                            )}
                            {!editing && (
                                <span className={isNew || contractReferenceLabel ? undefined : 'tw-text-charcoal-50'}>
                                    {isNew ? '' : contractReferenceLabel || $t('Select…')}
                                </span>
                            )}
                        </div>
                        {applyTrigger(
                            'contract',
                            $t('Contract Reference'),
                            props.data.typed_contractable_id,
                            contractReference(props.data.typed_contractable_id),
                        )}
                    </div>
                </td>
            )}
            {props.displayedColumns.amount && (
                <td className="inbud-numeric-cell" onClick={props.setEditedRow}>
                    {editing && (
                        <input
                            type="text"
                            inputMode="decimal"
                            className={`input text-input !tw-h-6 !tw-bg-transparent !tw-px-1.5 !tw-py-0 !tw-text-xs tw-text-end hover:!tw-bg-charcoal-10 ${amountOutOfRange ? 'invalid' : ''}`}
                            // An emptied field stays empty when focus leaves it:
                            // formatting '' would render "0.00", which reads as a
                            // saved zero and hides a state that cannot be saved.
                            value={
                                amountFocused || props.data.amount === '' || props.data.amount === null || props.data.amount === undefined
                                    ? (props.data.amount ?? '')
                                    : formatToFixedMoneyWithLocale(props.data.amount, 2)
                            }
                            onChange={onAmountChange}
                            onFocus={() => setAmountFocused(true)}
                            onBlur={() => setAmountFocused(false)}
                            aria-invalid={amountOutOfRange}
                            title={amountOutOfRange ? $t('This allocation is outside the amount still available on the transaction.') : undefined}
                            style={{ flexGrow: 1 }}
                        />
                    )}
                    {/* Precision 2, matching the edit-at-rest formatting a few lines up: before this
                        PR edit mode was a raw number input, so read and edit-at-rest never disagreed
                        on a formatted figure. Leaving this at the default precision 0 would render
                        1,234.56 as "1,235" here and "1,234.56" the instant the row enters edit mode. */}
                    {!editing && <span>{isNew ? '' : formatFigure(props.data.amount, formatToFixedMoneyWithLocale, { precision: 2 })}</span>}
                </td>
            )}
            {props.displayedColumns.splitAllocation && (
                <td onClick={props.setEditedRow}>
                    {!!loading && <LoadingSpinner />}
                    {!loading && (
                        <div className="tw-flex tw-items-center tw-justify-start tw-gap-x-2">
                            {props.index === 0 && props.isSelectable && (
                                <div onClick={(e) => e.stopPropagation()}>
                                    <CheckboxInput
                                        checked={props.selected}
                                        onChange={() => props.toggleSelection(props.actualCosts.id)}
                                        disabled={!props.isEligible}
                                    />
                                </div>
                            )}
                            {editing && (
                                <>
                                    <div className="tw-cursor-pointer">
                                        <TextButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                // Stop the malformed/emptied amount here rather than let it reach
                                                // the server as a 422 the row cannot recover from on its own.
                                                if (amountOutOfRange) {
                                                    toast.error($t('Enter a valid amount before confirming.'));
                                                    return;
                                                }
                                                setLoading(true);
                                                props.saveAllocations(null, () => {
                                                    setLoading(false);
                                                    props.resetEditedRow();
                                                });
                                            }}
                                            className="tw-flex tw-items-center"
                                        >
                                            <CircleCheckIcon className="tw-h-3 tw-w-3 tw-text-green-500" />
                                        </TextButton>
                                    </div>
                                    <div className="tw-cursor-pointer">
                                        <TextButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                props.cancelEdit();
                                            }}
                                            className="tw-flex tw-items-center"
                                        >
                                            <CircleCancelIcon className="tw-h-3 tw-w-3 tw-text-red-500" />
                                        </TextButton>
                                    </div>
                                </>
                            )}
                            <RowActionsMenu actions={rowActions} />
                        </div>
                    )}
                </td>
            )}
        </Fragment>
    );
});

export default ActualCostsAllocationSubRow;
